import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  ROLES_EMPLOYEUR,
  invitationsEmployeur,
  type MonEmployeur,
  type RoleEmployeur,
} from '@/services/invitationsEmployeur.service';

/**
 * Inviter quelqu'un — un écran PLEIN, qui remplace le précédent.
 *
 * Règle 14 de Rose (16/08/2026) : « une action = un écran plein qui REMPLACE le
 * précédent, jamais un panneau qui s'ajoute au-dessus d'une page déjà remplie ».
 * La première version ouvrait un formulaire dans la page « Mon entreprise », au
 * milieu de la liste des accès. On ne montre plus ici QUE l'invitation : ni la
 * liste, ni le reste.
 *
 * Deux champs, et c'est tout. Le rôle est prérempli sur celui qu'on invite le
 * plus souvent, et son effet est écrit sous le choix — la personne relit et
 * valide plutôt qu'elle ne cherche.
 */
export function InviterPage() {
  const naviguer = useNavigate();
  const [parametres] = useSearchParams();
  const [entreprises, setEntreprises] = useState<MonEmployeur[] | null>(null);
  const [entreprise, setEntreprise] = useState<MonEmployeur | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<RoleEmployeur>('drh');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    void invitationsEmployeur
      .miens()
      .then(liste => {
        setEntreprises(liste);
        const demandee = parametres.get('employeur');
        const selection = liste.find(e => e.uuid === demandee);
        setEntreprise(selection ?? (liste.length === 1 ? liste[0] : null));
      })
      .catch(() => {
        setEntreprises([]);
        setEntreprise(null);
      });
  }, [parametres]);

  const envoyer = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    if (!entreprise) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await invitationsEmployeur.inviter({
        employerUuid: entreprise.uuid,
        email,
        role,
      });
      // On revient là d'où l'on vient, avec la ligne ajoutée : la personne voit
      // le résultat de son geste, elle n'a rien à retenir.
      naviguer(
        `/mon-entreprise?entreprise=${encodeURIComponent(entreprise.uuid)}&invitee=${encodeURIComponent(email)}`
      );
    } catch (e: unknown) {
      setErreur(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "L'invitation n'a pas pu être envoyée."
      );
      setEnvoi(false);
    }
  };

  const aide = ROLES_EMPLOYEUR.find(r => r.valeur === role)?.aide;

  return (
    // Même marge gauche que « Mon entreprise » : les deux écrans se suivent,
    // ils doivent commencer au même endroit. Centrer le formulaire créait du
    // vide à gauche — le défaut que Rose signale le plus souvent.
    <div className="mx-auto max-w-5xl p-6">
      <div className="max-w-xl">
        <button
          type="button"
          onClick={() => naviguer('/mon-entreprise')}
          className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 transition-colors hover:text-neutral-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>

        <h1 className="text-2xl font-bold text-neutral-900">
          Inviter une personne
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          {entreprise
            ? `Elle rejoindra ${entreprise.nom}.`
            : entreprises === null
              ? 'Chargement de votre entreprise…'
              : entreprises.length > 1
                ? "Choisissez l'entreprise concernée."
                : "Aucune entreprise n'est disponible pour cette invitation."}
        </p>

        {entreprises && entreprises.length > 1 && (
          <div className="mt-6">
            <label
              htmlFor="entreprise"
              className="block text-sm font-medium text-neutral-700"
            >
              Entreprise
            </label>
            <select
              id="entreprise"
              value={entreprise?.uuid ?? ''}
              onChange={event =>
                setEntreprise(
                  entreprises.find(e => e.uuid === event.target.value) ?? null
                )
              }
              className="mt-2 block min-h-11 w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: 'var(--marque-trait)' }}
            >
              <option value="">Choisir une entreprise</option>
              {entreprises.map(e => (
                <option key={e.uuid} value={e.uuid}>
                  {e.nom}
                </option>
              ))}
            </select>
          </div>
        )}

        <form onSubmit={envoyer} className="mt-8">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-neutral-700"
          >
            Adresse e-mail
          </label>
          <input
            id="email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Adresse professionnelle"
            className="mt-2 block w-full rounded-lg border px-3 py-2 placeholder:text-neutral-400/60 focus:ring-2 focus:outline-none"
            style={{ borderColor: 'var(--marque-trait)' }}
          />

          <label
            htmlFor="role"
            className="mt-6 block text-sm font-medium text-neutral-700"
          >
            Rôle
          </label>
          <select
            id="role"
            value={role}
            onChange={e => setRole(e.target.value as RoleEmployeur)}
            className="mt-2 block w-full rounded-lg border px-3 py-2 focus:ring-2 focus:outline-none"
            style={{ borderColor: 'var(--marque-trait)' }}
          >
            {ROLES_EMPLOYEUR.map(r => (
              <option key={r.valeur} value={r.valeur}>
                {r.libelle}
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-neutral-500">{aide}</p>

          {erreur && (
            <p className="mt-6 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800">
              {erreur}
            </p>
          )}

          <div className="mt-8 flex items-center gap-4">
            <button
              type="submit"
              disabled={envoi || !entreprise}
              className="rounded-lg px-6 py-2 text-sm font-medium disabled:opacity-50"
              style={{
                backgroundColor: 'var(--marque-accent)',
                color: 'var(--marque-sur-accent)',
              }}
            >
              {envoi ? 'Envoi…' : "Envoyer l'invitation"}
            </button>
            <button
              type="button"
              onClick={() => naviguer('/mon-entreprise')}
              className="text-sm text-neutral-500 transition-colors hover:text-neutral-900"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
